import type { Situacao } from "@/api/contrato";

/*
 * A pílula de estado — doc 06a §3.2.
 *
 * "O trabalho inteiro do operador é ler estado em massa. Esta paleta importa
 * mais que qualquer outra decisão visual."
 *
 * Todas as situações renderizam TINGIDAS: fundo na matiz a 12%, texto a 100%,
 * borda a 25%, cantos totalmente arredondados.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ EXCETO O INDETERMINADO, QUE É SÓLIDO — E SÓ ELE.                        │
 * │                                                                          │
 * │ `INDETERMINADO` significa "o sistema não sabe se este boleto existe no   │
 * │ banco". É o estado mais raro e o mais perigoso do produto, e ele nunca   │
 * │ pode se camuflar numa varredura de mil linhas.                           │
 * │                                                                          │
 * │ A diferença é de FORMA, e não só de matiz: sobrevive a daltonismo, a     │
 * │ monitor ruim e a operador cansado às 18h. Nenhum outro elemento da       │
 * │ interface usa pílula sólida — gastar essa forma em outro lugar é gastar  │
 * │ o único sinal que não depende de cor.                                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

type Matiz = "transito" | "confirmado" | "pago" | "falha" | "encerrado" | "neutro";

const POR_SITUACAO: Record<string, Matiz> = {
  // Em trânsito, ainda não confirmado.
  RECEBIDO: "transito",
  ENFILEIRADO: "transito",
  ENVIADO: "transito",
  EMITIDO: "transito",

  // Confirmado no banco — o estado "normal bom".
  REGISTRADO: "confirmado",

  // Pago. Não lute contra essa convenção.
  LIQUIDADO: "pago",

  // Contido, não alarme de incêndio.
  FALHA: "falha",
  REJEITADO: "falha",
  /*
   * `PROTESTADO` não estava na tabela do doc 06a §3.2, que lista dez das doze
   * situações canônicas. Vai em vermelho contido junto de falha e rejeitado:
   * das duas famílias possíveis — "encerrado sem drama" e "exige atenção" —,
   * protesto é a segunda. Um título protestado tem consequência jurídica para
   * o sacado, e ele não pode ler como uma baixa rotineira.
   */
  PROTESTADO: "falha",

  // Encerrado sem pagamento, sem drama.
  BAIXADO: "encerrado",
  DESCARTADO: "encerrado",
  CANCELADO: "encerrado",
};

const TINGIDA: Record<Matiz, string> = {
  transito: "border-transito/25 bg-transito/12 text-transito",
  confirmado: "border-confirmado/25 bg-confirmado/12 text-confirmado",
  pago: "border-pago/25 bg-pago/12 text-pago",
  falha: "border-falha/25 bg-falha/12 text-falha",
  encerrado: "border-encerrado/25 bg-encerrado/12 text-encerrado",
  neutro: "border-fio-forte bg-superficie-2 text-texto-2",
};

const BASE =
  "inline-flex items-center rounded-full border px-2 py-[2px] text-rotulo " +
  "font-medium uppercase tracking-[0.08em] whitespace-nowrap";

export function PilulaDeSituacao({
  situacao,
  indeterminado = false,
}: {
  situacao: Situacao;
  /**
   * O boleto passou 96 h sem o provider confirmar nada (campo `indeterminado`,
   * que a coluna gerada do banco decide). Note que NÃO existe situação
   * `INDETERMINADO` na máquina de estados: é uma marca sobre um boleto que
   * continua em `ENVIADO` ou `EMITIDO`, e ela tem precedência sobre a situação
   * na leitura da tela.
   */
  indeterminado?: boolean;
}) {
  if (indeterminado) {
    return (
      <span
        className={`${BASE} border-indeterminado bg-indeterminado text-breu`}
        title={`Indeterminado — situação registrada: ${situacao}`}
      >
        Indeterminado
      </span>
    );
  }

  // Situação desconhecida degrada para neutra COM O TEXTO CRU, e não some nem
  // vira "—": o conjunto de situações é aberto (doc 02 §8), e uma versão nova da
  // API precisa aparecer na tela em vez de sumir dela.
  const matiz = POR_SITUACAO[situacao] ?? "neutro";

  return <span className={`${BASE} ${TINGIDA[matiz]}`}>{rotulo(situacao)}</span>;
}

/**
 * Pílula tingida para estado que não é de boleto — a situação de um contrato,
 * a disponibilidade de um plano. Mesma forma e mesma paleta, para que "vigente"
 * e "encerrado" se leiam na mesma varredura que "Registrado" e "Baixado".
 */
export function Pilula({ matiz, children }: { matiz: Matiz; children: string }) {
  return <span className={`${BASE} ${TINGIDA[matiz]}`}>{children}</span>;
}

/**
 * Estado normal não se anuncia: a pílula só aparece quando foge do normal.
 *
 * Numa lista de duzentos convênios, duzentos selos `ativo` escondem o único bloqueado, e
 * ao lado do interruptor a palavra só repete a posição dele. O texto continua para quem
 * usa leitor de tela, que não vê o interruptor nem a ausência da pílula.
 */
export function PilulaDeExcecao({
  normal,
  matiz,
  children,
}: {
  normal: boolean;
  matiz: Matiz;
  children: string;
}) {
  if (normal) return <span className="sr-only">{children}</span>;
  return <Pilula matiz={matiz}>{children}</Pilula>;
}

export type { Matiz };

function rotulo(situacao: string): string {
  return situacao.charAt(0) + situacao.slice(1).toLowerCase();
}
